import { useMutation } from '@apollo/client'
import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Grid, Typography } from '@material-ui/core'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { RemoveItemFromCartDocument } from '../../../graphql/magento'
import CheckoutLayout from '../../../layouts/buy/CheckoutLayout'
import { TokenContext } from '../../../layouts/context'
import {
  dropShipFieldValuesFromCart,
  fieldValuesFromCartAddress,
  fieldValuesFromCustomerAddress,
  requiredAddressFieldValues,
} from '../../../lib/cart/address'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import useCheckoutNavBarOptionValue from '../../../lib/cart/hooks/useCheckoutNavBarOptionValue'
import { useDefaultCountryCode } from '../../../lib/cart/hooks/useDefaultCountryCode'
import { useShippingCountries } from '../../../lib/cart/hooks/useShippingCountries'
import {
  extractCartRestrictedItems,
  filterSanityRestrictedSkus,
  SanityRestrictedSkusFilters,
} from '../../../lib/cart/restricted-items'
import {
  AddressRestrictions,
  CartShippingAddress,
  CustomerAddressFromQuery,
  FormValues,
  RestrictedItem,
  ValidFormValues,
} from '../../../lib/cart/types'
import { assertRequiredFields } from '../../../utils/assertTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import ApplicationErrorMessage from '../../ApplicationErrorMessage'
import ErrorUpdatingCartMessage from '../../ErrorUpdatingCartMessage'
import Spinner from '../../Spinner'
import AddressFormCheckout from '../shared/AddressFormCheckout'
import { GridButton } from '../shared/GridButton'
import DropShipForm from './DropShipForm'
import { CountryBannedModal } from './modals/CountryBannedModal'
import { CountryShippingRestrictionsModal } from './modals/CountryShippingRestrictionsModal'
import { RegionShippingRestrictionsModal } from './modals/RegionShippingRestrictionsModal'

export interface ShippingDetailsProps {
  addressRestrictions: AddressRestrictions
  defaultShippingAddress?: CustomerAddressFromQuery
  error?: Error
  onSubmit: (values: ValidFormValues.ShippingDetails) => void
  submitting: boolean
  shippingAddress?: CartShippingAddress
}

const ShippingDetails: React.FC<ShippingDetailsProps> = ({
  addressRestrictions,
  defaultShippingAddress,
  error,
  onSubmit,
  submitting,
  shippingAddress,
}) => {
  const token = useContext(TokenContext)
  const customerCart = useContext(NonEmptyCartContext)
  const defaultCountryCode = useDefaultCountryCode()
  const shippingCountries = useShippingCountries()

  const defaultAddressValues = useMemo(
    () =>
      fieldValuesFromCartAddress(
        shippingAddress,
        defaultShippingAddress
          ? fieldValuesFromCustomerAddress(defaultShippingAddress)
          : { country_code: defaultCountryCode },
      ),
    [defaultCountryCode, defaultShippingAddress, shippingAddress],
  )

  const defaultDropShipValues = useMemo(() => {
    return dropShipFieldValuesFromCart(customerCart)
  }, [customerCart])
  const methods = useForm<FormValues.Address & FormValues.DropShip>({
    mode: 'onBlur',
    defaultValues: defaultAddressValues,
  })

  const { navBarOptionValue, loading: loadingNavBarOptions } =
    useCheckoutNavBarOptionValue('shipping')
  const [openRegionShippingRestrictionsModal, setOpenRegionShippingRestrictionsModal] =
    useState(false)
  const [openCountryShippingRestrictionsModal, setOpenCountryShippingRestrictionsModal] =
    useState(false)
  const [openCountryIsBannedModal, setOpenCountryIsBannedModal] = useState(false)
  const [shippingRestrictionsLoading, setShippingRestrictionsLoading] = useState(false)
  const [runTypeError, setRunTypeError] = useState(false)
  const formErrorsAlertRef = useRef<HTMLDivElement>(null)
  const restrictedItems = useRef<RestrictedItem[]>([])
  const region = methods.watch('region')

  const [removeItemFromCart, { error: removeItemError }] = useMutation(RemoveItemFromCartDocument, {
    context: { token },
  })

  const handleRemoveItems = useCallback(() => {
    setShippingRestrictionsLoading(true)
    restrictedItems.current
      .reduce(async (acc, item) => {
        await acc
        await removeItemFromCart({
          variables: {
            cartId: customerCart.id,
            itemId: Number(item.id),
          },
        })
      }, Promise.resolve())
      .then(() => {
        setShippingRestrictionsLoading(false)
        setOpenRegionShippingRestrictionsModal(false)
        setOpenCountryShippingRestrictionsModal(false)
      })
      .catch((err) => {
        logAndCaptureException(err)
        setShippingRestrictionsLoading(false)
        setOpenRegionShippingRestrictionsModal(false)
        setOpenCountryShippingRestrictionsModal(false)
      })
  }, [customerCart.id, removeItemFromCart])

  const hasFormErrors = useMemo(
    () => Object.keys(methods.formState.errors).length > 0,
    [methods.formState],
  )

  const isShippingRestrictedForCountry = useCallback(
    (countryCode: string) =>
      shippingCountries.find(
        (country) => country.country === countryCode && country.isShippingRestricted,
      ),
    [shippingCountries],
  )

  const isCartShippingRestrictedForRegion = useCallback(
    (filters: SanityRestrictedSkusFilters) => {
      const restrictedSkus = filterSanityRestrictedSkus(shippingCountries)(filters)
      restrictedItems.current = extractCartRestrictedItems({
        customerCart,
        restrictedSkus,
      })
      return !!restrictedItems.current.length
    },
    [customerCart, shippingCountries],
  )

  const isShippingRestrictedForFormCountry = useCallback(
    (formCountryCode: string) =>
      // Show the banned-country modal if we can't ship to the selected country at all
      // Guam, Hong Kong, & Virgin Islands are country options & must be converted to regions
      (formCountryCode === 'HK' && isShippingRestrictedForCountry('CN')) ||
      (formCountryCode === 'GU' && isShippingRestrictedForCountry('US')) ||
      (formCountryCode === 'VI' && isShippingRestrictedForCountry('US')) ||
      isShippingRestrictedForCountry(formCountryCode),
    [isShippingRestrictedForCountry],
  )

  const isCartShippingRestrictedForAllCountryRegions = useCallback(
    (formCountryCode: string) =>
      // Show the banned-product modal if we can't send one in the cart to the selected country
      isCartShippingRestrictedForRegion({
        country: formCountryCode,
        type: 'all-regions',
      }),
    [isCartShippingRestrictedForRegion],
  )

  const isCartShippingRestrictedForFormRegion = useCallback(
    (formCountryCode: string, formRegion: string) =>
      // Show the shipping restriction modal if a product is banned from the selected region
      (formCountryCode === 'HK' &&
        isCartShippingRestrictedForRegion({ country: 'CN', region: 'HK' })) ||
      (formCountryCode === 'GU' &&
        isCartShippingRestrictedForRegion({ country: 'US', region: 'GU' })) ||
      (formCountryCode === 'VI' &&
        isCartShippingRestrictedForRegion({ country: 'US', region: 'VI' })) ||
      isCartShippingRestrictedForRegion({
        country: formCountryCode,
        region: formRegion,
      }),
    [isCartShippingRestrictedForRegion],
  )

  const openModalIfShippingRestricted = useCallback(
    (formCountryCode: string, formRegion: string) => {
      if (isShippingRestrictedForFormCountry(formCountryCode)) {
        setOpenCountryIsBannedModal(true)
        return true
      }
      if (isCartShippingRestrictedForAllCountryRegions(formCountryCode)) {
        setOpenCountryShippingRestrictionsModal(true)
        return true
      }
      if (isCartShippingRestrictedForFormRegion(formCountryCode, formRegion)) {
        setOpenRegionShippingRestrictionsModal(true)
        return true
      }
      return false
    },
    [
      isCartShippingRestrictedForAllCountryRegions,
      isCartShippingRestrictedForFormRegion,
      isShippingRestrictedForFormCountry,
    ],
  )

  const handleSubmit = useCallback(
    async (e?: React.FormEvent | React.MouseEvent) => {
      e?.preventDefault()
      setRunTypeError(false)

      const formValues = methods.getValues()

      const isValid = await methods.trigger()

      if (!isValid) {
        const formErrorsEl = formErrorsAlertRef.current
        if (formErrorsEl) {
          formErrorsEl.scrollIntoView({ behavior: 'smooth' })
        }
        return
      }

      try {
        assertRequiredFields(formValues, requiredAddressFieldValues)
      } catch (err) {
        logAndCaptureException(err)
        setRunTypeError(true)
        return
      }

      const hasOpenedModal = openModalIfShippingRestricted(
        formValues.country_code,
        formValues.region,
      )
      if (hasOpenedModal) {
        return
      }

      onSubmit(formValues)
    },
    [methods, onSubmit, openModalIfShippingRestricted],
  )

  if (loadingNavBarOptions) return <Spinner loading />

  return (
    <CheckoutLayout checkoutNavigationBarTabValue={navBarOptionValue}>
      {error && (
        <Grid item xs={12}>
          <Alert type="error">
            <ErrorUpdatingCartMessage />
          </Alert>
        </Grid>
      )}
      <Grid
        alignItems="center"
        justifyContent="center"
        container
        item
        xs={12}
        component="form"
        noValidate
        onSubmit={handleSubmit}
      >
        <Grid item xs={12}>
          <Typography variant="h5" data-cy="checkout-shipping-details-title">
            SHIPPING DETAILS
          </Typography>
        </Grid>
        <Grid ref={formErrorsAlertRef} item xs={12}>
          {hasFormErrors && (
            <Alert type="error">
              Invalid shipping details. Please check the highlighted fields for errors.
            </Alert>
          )}
          {runTypeError && (
            <Alert type="error">
              <ApplicationErrorMessage />
            </Alert>
          )}
        </Grid>
        <Grid container item xs={12}>
          <FormProvider {...methods}>
            <AddressFormCheckout
              addressRestrictions={addressRestrictions}
              defaultValues={defaultAddressValues}
            />
            <DropShipForm defaultValues={defaultDropShipValues} />
          </FormProvider>
        </Grid>
        <GridButton
          disabled={submitting}
          onClick={handleSubmit}
          type="submit"
          id="continue-to-delivery-button"
          data-cy="go-to-delivery"
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'CONTINUE TO DELIVERY'}
        </GridButton>
      </Grid>
      <RegionShippingRestrictionsModal
        open={openRegionShippingRestrictionsModal}
        handleClose={() => setOpenRegionShippingRestrictionsModal(false)}
        handleContinue={() => setOpenRegionShippingRestrictionsModal(false)}
        handleRemoveItems={handleRemoveItems}
        removeItemsError={removeItemError}
        restrictedItems={restrictedItems.current}
        loading={shippingRestrictionsLoading}
        region={region}
      />
      <CountryShippingRestrictionsModal
        open={openCountryShippingRestrictionsModal}
        handleClose={() => setOpenCountryShippingRestrictionsModal(false)}
        handleContinue={() => setOpenCountryShippingRestrictionsModal(false)}
        handleRemoveItems={handleRemoveItems}
        removeItemsError={removeItemError}
        restrictedItems={restrictedItems.current}
        loading={shippingRestrictionsLoading}
      />
      <CountryBannedModal
        open={openCountryIsBannedModal}
        handleClose={() => setOpenCountryIsBannedModal(false)}
        handleContinue={() => setOpenCountryIsBannedModal(false)}
      />
    </CheckoutLayout>
  )
}

export default ShippingDetails
